import { supabase } from '@/integrations/supabase/client';

import { bootOrRequest, bootReadKey } from './boot-read-registry';

import type { Session } from '@supabase/supabase-js';

// The requests the signed-in shell makes first. Each is built here, and not in the query that
// consumes it, so the entry bundle can start one before React renders without importing the
// hooks' dependency graph. The consumers post-process the response as they always did.

/** Pins a request to one access token, so its answer belongs to the session that was checked. */
function pinnedTo<Builder extends { setHeader: (name: string, value: string) => Builder }>(
  builder: Builder,
  accessToken: string | undefined,
): Builder {
  return accessToken ? builder.setHeader('Authorization', `Bearer ${accessToken}`) : builder;
}

export function requestUserOrganizations(userId: string, accessToken?: string) {
  return pinnedTo(
    supabase
      .from('organization_members')
      .select(
        `
          id, organization_id, user_id, role, status, joined_at, created_at, updated_at,
          organization:organizations (
            id, name, slug, logo_url, is_active, description, settings, owner_id, created_at, updated_at
          )
        `,
      )
      .eq('user_id', userId)
      // Pending memberships have no RPC grants; including them produces
      // guaranteed 403s on every email-list / counts / search call. Pending
      // invitations belong on a separate read path (not yet implemented).
      .eq('status', 'active')
      .order('joined_at', { ascending: false }),
    accessToken,
  );
}

export function requestIsPlatformAdmin(accessToken?: string) {
  return pinnedTo(supabase.rpc('is_admin').single(), accessToken);
}

export function requestTeamMembers(organizationId: string, accessToken?: string) {
  // Single query via team_members view (server-side JOIN of organization_members + profiles).
  return pinnedTo(
    supabase
      .from('team_members')
      .select(
        'id, user_id, organization_id, role, status, joined_at, created_at, updated_at, first_name, last_name, email, avatar_url',
      )
      .eq('organization_id', organizationId),
    accessToken,
  );
}

export function requestFeatureGateAccess(organizationId: string, accessToken?: string) {
  return pinnedTo(
    supabase.rpc('feature_gate_access', { p_organization_id: organizationId }),
    accessToken,
  );
}

/**
 * The client's current session when it belongs to `userId`. The client refreshes an expired token
 * here, so a boot read is pinned to a token that is live and that user's: a session that changed
 * under the stored one gets no boot read, rather than another person's answer.
 */
export async function sessionOf(userId: string): Promise<Session | undefined> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id === userId ? data.session : undefined;
}

/**
 * Calls `listener` when the client sees a sign-out, including one made in another tab. The app's
 * own sign-out signal does not exist until React mounts, so a boot read watches for it itself.
 */
export function onBootSignOut(listener: () => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      listener();
    }
  });
  return () => {
    data.subscription.unsubscribe();
  };
}

export type UserOrganizationsResponse = Awaited<ReturnType<typeof requestUserOrganizations>>;
export type PlatformAdminResponse = Awaited<ReturnType<typeof requestIsPlatformAdmin>>;
export type TeamMembersResponse = Awaited<ReturnType<typeof requestTeamMembers>>;
export type FeatureGateAccessResponse = Awaited<ReturnType<typeof requestFeatureGateAccess>>;

export type FeatureGateAccessReader = (
  organizationId: string,
) => PromiseLike<FeatureGateAccessResponse>;

/** Reads an office's gates for a user: the boot read's answer when one was started. */
export function featureGateAccessReader(userId: string): FeatureGateAccessReader {
  return (organizationId) =>
    bootOrRequest<FeatureGateAccessResponse>(bootReadKey.featureGates(userId, organizationId), () =>
      requestFeatureGateAccess(organizationId),
    );
}
